import React, { useMemo, useState } from 'react';
import {
  Steps, Upload, Button, Card, Select, Table, Tag, Alert, Typography, Space, message,
  Statistic, Row, Col, Collapse, Divider, List,
} from 'antd';
import { InboxOutlined, DownloadOutlined, UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { parseCSV } from '../../utils/csv';
import {
  IMPORT_FIELDS, FieldGroup, importEmployees, TEMPLATES, ChildData,
  ImportResponse, RowResult,
} from '../../services/employeeImportService';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const actionColor: Record<string, string> = { create: 'green', update: 'blue', skip: 'red' };

const GROUP_LABELS: Record<FieldGroup, string> = {
  employee: 'Employee details',
  address: 'Address (optional)',
  emergency: 'Emergency contacts (optional)',
  education: 'Education (optional)',
};

type ChildKind = 'addresses' | 'emergency_contacts' | 'education';
const CHILD_LABELS: Record<ChildKind, string> = {
  addresses: 'Addresses',
  emergency_contacts: 'Emergency contacts',
  education: 'Education',
};

export const BulkImportPage: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [childRows, setChildRows] = useState<ChildData>({});
  const [childNames, setChildNames] = useState<Record<ChildKind, string>>({ addresses: '', emergency_contacts: '', education: '' });
  const [dryResult, setDryResult] = useState<ImportResponse | null>(null);
  const [commitResult, setCommitResult] = useState<ImportResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const requiredKeys = IMPORT_FIELDS.filter((f) => f.required).map((f) => f.key);
  const requiredMapped = requiredKeys.every((k) => mapping[k]);

  const mappedRows = useMemo(
    () =>
      rawRows.map((r) => {
        const out: Record<string, string> = {};
        IMPORT_FIELDS.forEach((f) => {
          const col = mapping[f.key];
          if (col && r[col] != null && r[col] !== '') out[f.key] = r[col];
        });
        return out;
      }),
    [rawRows, mapping],
  );

  const handleMainFile = async (file: File) => {
    try {
      const { headers: h, rows } = parseCSV(await file.text());
      if (!h.length || !rows.length) { message.error('That file has no data rows.'); return false; }
      setFileName(file.name); setHeaders(h); setRawRows(rows);
      const guess: Record<string, string> = {};
      IMPORT_FIELDS.forEach((f) => {
        const match = h.find((col) => norm(col) === norm(f.key) || norm(col) === norm(f.label));
        if (match) guess[f.key] = match;
      });
      setMapping(guess); setDryResult(null); setCommitResult(null);
      message.success(`Loaded ${rows.length} rows from ${file.name}`);
    } catch { message.error('Could not read that file.'); }
    return false;
  };

  const handleChildFile = (kind: ChildKind) => async (file: File) => {
    try {
      const { rows } = parseCSV(await file.text());
      setChildRows((c) => ({ ...c, [kind]: rows }));
      setChildNames((n) => ({ ...n, [kind]: file.name }));
      message.success(`Loaded ${rows.length} ${CHILD_LABELS[kind].toLowerCase()} rows`);
    } catch { message.error('Could not read that file.'); }
    return false;
  };

  const download = (name: string, content: string) => {
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const run = async (dry: boolean) => {
    setLoading(true);
    try {
      const res = await importEmployees(mappedRows, dry, childRows);
      if (dry) { setDryResult(res); setCurrent(2); }
      else {
        setCommitResult(res); setCurrent(3);
        message.success(`Imported ${res.summary.to_create} new and updated ${res.summary.to_update} employees.`);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Request failed. Check your sign-in and try again.');
    } finally { setLoading(false); }
  };

  const resultColumns = [
    { title: 'Row', dataIndex: 'row', width: 60 },
    { title: 'Name', dataIndex: 'name', render: (v: string) => v || <Text type="secondary">—</Text> },
    { title: 'Email', dataIndex: 'email', render: (v: string) => v || <Text type="secondary">—</Text> },
    { title: 'Action', dataIndex: 'action', width: 100, render: (v: string) => <Tag color={actionColor[v]}>{v.toUpperCase()}</Tag> },
    {
      title: 'Issues',
      render: (_: any, r: RowResult) => (
        <Space direction="vertical" size={0}>
          {r.errors.map((e, i) => <Text key={`e${i}`} type="danger">{e}</Text>)}
          {r.warnings.map((w, i) => <Text key={`w${i}`} type="warning">{w}</Text>)}
          {!r.errors.length && !r.warnings.length && <Text type="secondary">—</Text>}
        </Space>
      ),
    },
  ];

  const Summary: React.FC<{ res: ImportResponse }> = ({ res }) => (
    <>
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col span={6}><Card size="small"><Statistic title="Total rows" value={res.summary.total} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="To create" value={res.summary.to_create} valueStyle={{ color: 'var(--color-success)' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="To update" value={res.summary.to_update} valueStyle={{ color: '#1677ff' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="Skipped" value={res.summary.skipped} valueStyle={{ color: res.summary.skipped ? 'var(--color-danger)' : undefined }} /></Card></Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}><Card size="small"><Statistic title="Addresses" value={res.summary.addresses} /></Card></Col>
        <Col span={8}><Card size="small"><Statistic title="Emergency contacts" value={res.summary.emergency_contacts} /></Card></Col>
        <Col span={8}><Card size="small"><Statistic title="Education records" value={res.summary.education} /></Card></Col>
      </Row>
    </>
  );

  const ChildIssues: React.FC<{ res: ImportResponse }> = ({ res }) =>
    res.child_issues.length ? (
      <Alert
        style={{ margin: '16px 0' }}
        type="warning"
        showIcon
        message={`${res.child_issues.length} sub-resource row(s) were skipped`}
        description={<List size="small" dataSource={res.child_issues} renderItem={(i) => <List.Item>{i}</List.Item>} />}
      />
    ) : null;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Title level={3}>Bulk employee import</Title>
      <Paragraph type="secondary">
        Upload a CSV of your existing employees — including addresses, emergency contacts and education either as columns on the
        employee row or as separate files. We validate everything and show a preview before anything is saved. Re-uploading is safe:
        matching emails are updated (and their sub-resources replaced), never duplicated.
      </Paragraph>

      <Steps current={current} style={{ margin: '24px 0' }}
        items={[{ title: 'Upload' }, { title: 'Map columns' }, { title: 'Preview' }, { title: 'Done' }]} />

      {/* Step 0: Upload */}
      {current === 0 && (
        <Card>
          <Dragger accept=".csv" maxCount={1} beforeUpload={handleMainFile} showUploadList={false}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag your employees CSV here</p>
            <p className="ant-upload-hint">{fileName ? `Loaded: ${fileName} (${rawRows.length} rows)` : 'Required. The other files below are optional.'}</p>
          </Dragger>
          <Space style={{ marginTop: 12 }}>
            <Button icon={<DownloadOutlined />} onClick={() => download('employees-template.csv', TEMPLATES.employees())}>Employees template</Button>
          </Space>

          <Divider>Optional: separate files for unbounded sub-resources</Divider>
          <Paragraph type="secondary" style={{ marginTop: -8 }}>
            Use these only if someone has more addresses/contacts/qualifications than fit in the employee row. Each row is keyed by <Text code>employee_email</Text>.
          </Paragraph>
          <Row gutter={16}>
            {(['addresses', 'emergency_contacts', 'education'] as ChildKind[]).map((kind) => (
              <Col span={8} key={kind}>
                <Card size="small" title={CHILD_LABELS[kind]}>
                  <Upload accept=".csv" maxCount={1} beforeUpload={handleChildFile(kind)} showUploadList={false}>
                    <Button icon={<UploadOutlined />} block>Upload CSV</Button>
                  </Upload>
                  <div style={{ marginTop: 8 }}>
                    {childNames[kind]
                      ? <Text type="success">{childNames[kind]} ({(childRows[kind] || []).length})</Text>
                      : <Text type="secondary">Not added</Text>}
                  </div>
                  <Button type="link" size="small" icon={<DownloadOutlined />} style={{ paddingLeft: 0 }}
                    onClick={() => download(`${kind}-template.csv`, TEMPLATES[kind]())}>Template</Button>
                </Card>
              </Col>
            ))}
          </Row>

          <Space style={{ marginTop: 16 }}>
            <Button type="primary" disabled={rawRows.length === 0} onClick={() => setCurrent(1)}>Next: map columns</Button>
          </Space>
        </Card>
      )}

      {/* Step 1: Map columns (grouped) */}
      {current === 1 && (
        <Card>
          <Paragraph>Match each field to a column from your employees file. Required fields are marked <Text type="danger">*</Text>; sub-resource groups are optional.</Paragraph>
          <Collapse defaultActiveKey={['employee']} items={(['employee', 'address', 'emergency', 'education'] as FieldGroup[]).map((g) => ({
            key: g,
            label: GROUP_LABELS[g],
            children: (
              <Row gutter={[16, 12]}>
                {IMPORT_FIELDS.filter((f) => f.group === g).map((f) => (
                  <Col span={12} key={f.key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 160 }}>
                        <Text strong>{f.label}</Text>{f.required && <Text type="danger"> *</Text>}
                        {f.hint && <div><Text type="secondary" style={{ fontSize: 12 }}>{f.hint}</Text></div>}
                      </div>
                      <Select style={{ flex: 1 }} allowClear placeholder="— not mapped —"
                        value={mapping[f.key] || undefined}
                        onChange={(v) => setMapping((m) => ({ ...m, [f.key]: v || '' }))}
                        options={headers.map((h) => ({ label: h, value: h }))}
                        status={f.required && !mapping[f.key] ? 'error' : undefined} />
                    </div>
                  </Col>
                ))}
              </Row>
            ),
          }))} />
          {!requiredMapped && <Alert style={{ marginTop: 16 }} type="warning" showIcon message="Map Email, First name and Last name to continue." />}
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setCurrent(0)}>Back</Button>
            <Button type="primary" loading={loading} disabled={!requiredMapped} onClick={() => run(true)}>Validate &amp; preview</Button>
          </Space>
        </Card>
      )}

      {/* Step 2: Preview */}
      {current === 2 && dryResult && (
        <Card>
          <Summary res={dryResult} />
          {dryResult.summary.skipped > 0 && (
            <Alert style={{ margin: '16px 0' }} type="warning" showIcon
              message={`${dryResult.summary.skipped} employee row(s) have errors and will be skipped.`} />
          )}
          <ChildIssues res={dryResult} />
          <Table rowKey="row" size="small" columns={resultColumns as any} dataSource={dryResult.results} pagination={{ pageSize: 10 }} />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setCurrent(1)}>Back</Button>
            <Button type="primary" icon={<UploadOutlined />} loading={loading}
              disabled={(dryResult.summary.to_create + dryResult.summary.to_update) === 0}
              onClick={() => run(false)}>
              Import {dryResult.summary.to_create + dryResult.summary.to_update} employees
            </Button>
          </Space>
        </Card>
      )}

      {/* Step 3: Done */}
      {current === 3 && commitResult && (
        <Card>
          <Alert type="success" showIcon icon={<CheckCircleOutlined />} message="Import complete"
            description={`Created ${commitResult.summary.to_create} and updated ${commitResult.summary.to_update} employees. Addresses ${commitResult.summary.addresses}, emergency contacts ${commitResult.summary.emergency_contacts}, education ${commitResult.summary.education}.`}
            style={{ marginBottom: 16 }} />
          <Summary res={commitResult} />
          <ChildIssues res={commitResult} />
          <Table rowKey="row" size="small" columns={resultColumns as any} dataSource={commitResult.results} pagination={{ pageSize: 10 }} style={{ marginTop: 16 }} />
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => {
              setCurrent(0); setRawRows([]); setHeaders([]); setMapping({}); setFileName('');
              setChildRows({}); setChildNames({ addresses: '', emergency_contacts: '', education: '' });
              setDryResult(null); setCommitResult(null);
            }}>Import another file</Button>
          </Space>
        </Card>
      )}
    </div>
  );
};

export default BulkImportPage;
